import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/axios';
import Modal from '../../components/ui/Modal';

const schema = z.object({
  name:      z.string().min(1, 'Required'),
  email:     z.string().email('Invalid email').or(z.literal('')).optional(),
  phone:     z.string().max(30).optional(),
  color:     z.string().regex(/^#[0-9a-fA-F]{6}$/),
  bio:       z.string().max(500).optional(),
  active:    z.boolean().default(true),
});

const COLORS = ['#6366F1','#EC4899','#F59E0B','#10B981','#3B82F6','#EF4444','#8B5CF6','#14B8A6'];

export default function StaffEditModal({ open, member, onClose }) {
  const qc    = useQueryClient();
  const isNew = !member?.id;

  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { color: '#6366F1', active: true },
  });

  const color = watch('color');

  useEffect(() => {
    if (open) reset(member?.id ? {
      name: member.name, email: member.email || '', phone: member.phone || '',
      color: member.color, bio: member.bio || '', active: member.active,
    } : { color: '#6366F1', active: true });
  }, [open, member, reset]);

  const mut = useMutation({
    mutationFn: (data) => isNew ? api.post('/staff', data) : api.put(`/staff/${member.id}`, data),
    onSuccess:  () => { qc.invalidateQueries(['staff']); onClose(); },
  });

  return (
    <Modal open={open} onClose={onClose} title={isNew ? 'Add staff member' : 'Edit staff member'} size="md">
      <form onSubmit={handleSubmit(d => mut.mutateAsync(d))} className="space-y-4">
        <div>
          <label className="label">Full name</label>
          <input {...register('name')} className={`input ${errors.name ? 'border-red-400' : ''}`} placeholder="Priya Sharma" />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Email</label>
            <input {...register('email')} type="email" className="input" placeholder="priya@studio.com" />
          </div>
          <div>
            <label className="label">Phone</label>
            <input {...register('phone')} type="tel" className="input" placeholder="+1 555 0100" />
          </div>
        </div>

        <div>
          <label className="label">Bio (optional)</label>
          <textarea {...register('bio')} rows={2} className="input resize-none" placeholder="A short bio shown to clients…" />
        </div>

        <div>
          <label className="label">Calendar color</label>
          <div className="flex items-center gap-2 flex-wrap">
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => setValue('color', c)}
                className={`w-7 h-7 rounded-full transition-all ${color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''}`}
                style={{ background: c }} />
            ))}
            <input type="color" value={color} onChange={e => setValue('color', e.target.value)}
              className="w-7 h-7 rounded-full border-0 p-0 cursor-pointer" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input {...register('active')} type="checkbox" id="staff-active" className="w-4 h-4 accent-primary" />
          <label htmlFor="staff-active" className="text-sm text-ink">Active</label>
        </div>

        {mut.isError && <p className="text-xs text-red-500">{mut.error?.response?.data?.error?.message || 'Save failed'}</p>}

        <div className="flex justify-end gap-3 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving…' : isNew ? 'Add member' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
